import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import UserDetailsModal from '../components/UserDetailsModal';
import { 
  Users, Package, Eye, Activity, Trash2, Search, 
  LogOut, Shield, TrendingUp, ChevronLeft, ChevronRight,
  Loader2, CheckCircle, XCircle, Download, BarChart3, FolderTree,
  Crown, CreditCard, FileCheck, UserCog, Smartphone, Bot, AlertTriangle, Car,
  Banknote, PieChart, Scissors, Settings
} from 'lucide-react';
import axios from 'axios';
import { useCountry } from '../context/CountryContext';

const AdminDashboard = () => {
  const { formatCurrency } = useCountry();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [listings, setListings] = useState([]);
  const [activities, setActivities] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserModal, setShowUserModal] = useState(false);

  useEffect(() => {
    checkAuth();
    fetchDashboardData();
    fetchAnalytics();
  }, [currentPage]);

  const checkAuth = () => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
    }
  };

  const getAuthHeader = () => {
    const token = localStorage.getItem('admin_token');
    return { Authorization: `Bearer ${token}` };
  };

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const headers = getAuthHeader();

      const [statsRes, usersRes, listingsRes, activitiesRes] = await Promise.all([
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/admin/analytics/dashboard`, { headers }),
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/admin/users?page=${currentPage}&limit=10`, { headers }),
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/admin/listings?limit=10`, { headers }),
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/admin/activity-logs?limit=10`, { headers })
      ]);

      setStats(statsRes.data);
      setUsers(usersRes.data.users);
      setTotalPages(usersRes.data.pages);
      setListings(listingsRes.data.listings);
      setActivities(activitiesRes.data.activities);
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      if (err.response?.status === 401 || err.response?.status === 403) {
        handleLogout();
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchAnalytics = async () => {
    try {
      const headers = getAuthHeader();
      const [regAnalytics, behaviorAnalytics] = await Promise.all([
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/admin/analytics/registration?days=30`, { headers }),
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/admin/analytics/user-behavior?days=30`, { headers })
      ]);

      setAnalytics({
        registration: regAnalytics.data,
        behavior: behaviorAnalytics.data
      });
    } catch (err) {
      console.error('Error fetching analytics:', err);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_user');
    navigate('/admin/login');
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user and all their listings?')) {
      return;
    }

    try {
      await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/users/${userId}`,
        { headers: getAuthHeader() }
      );
      alert('User deleted successfully');
      fetchDashboardData();
    } catch (err) {
      alert('Failed to delete user');
    }
  };

  const handleDeleteListing = async (listingId) => {
    if (!window.confirm('Are you sure you want to delete this listing?')) {
      return;
    }

    try {
      await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/listings/${listingId}`,
        { headers: getAuthHeader() }
      );
      alert('Listing deleted successfully');
      fetchDashboardData();
    } catch (err) {
      alert('Failed to delete listing');
    }
  };

  const handleExportUsers = async () => {
    try {
      const headers = getAuthHeader();
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/analytics/export/users?format=json`,
        { headers }
      );
      
      const dataStr = JSON.stringify(response.data.users, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `users_export_${new Date().toISOString()}.json`;
      link.click();
    } catch (err) {
      alert('Failed to export users');
    }
  };

  const handleViewUser = (user) => {
    setSelectedUser(user);
    setShowUserModal(true);
  };

  const getStatusBadge = (status) => {
    const colors = {
      active: 'bg-green-500',
      suspended: 'bg-yellow-500',
      banned: 'bg-red-500',
      inactive: 'bg-gray-500'
    };
    return <Badge className={colors[status] || colors.active}>{status?.toUpperCase() || 'ACTIVE'}</Badge>;
  };

  if (loading && !stats) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="h-12 w-12 animate-spin text-purple-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white">
        <div className="max-w-7xl mx-auto px-4 py-6">
          {/* Title Row */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-3">
              <Shield className="h-8 w-8" />
              <div>
                <h1 className="text-2xl font-bold">Admin Dashboard</h1>
                <p className="text-purple-100 text-sm">zeilian.co Management Portal</p>
              </div>
            </div>
            <Button 
              variant="outline" 
              onClick={handleLogout}
              className="bg-white/10 hover:bg-white/20 border-white/30 text-white"
            >
              <LogOut className="h-4 w-4 mr-2" />
              Logout
            </Button>
          </div>
          
          {/* Navigation Row - Below Title */}
          <div className="flex flex-wrap gap-2">
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => navigate('/admin/ads')}
              className="bg-white/10 hover:bg-white/20 border-white/30 text-white"
            >
              <Package className="h-4 w-4 mr-1" />
              Ads
            </Button>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => navigate('/admin/categories')}
              className="bg-white/10 hover:bg-white/20 border-white/30 text-white"
            >
              <FolderTree className="h-4 w-4 mr-1" />
              Categories
            </Button>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => navigate('/admin/memberships')}
              className="bg-white/10 hover:bg-white/20 border-white/30 text-white"
            >
              <Crown className="h-4 w-4 mr-1" />
              Memberships
            </Button>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => navigate('/admin/payment-verification')}
              className="bg-white/10 hover:bg-white/20 border-white/30 text-white"
            >
              <CreditCard className="h-4 w-4 mr-1" />
              Payments
            </Button>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => navigate('/admin/staff')}
              className="bg-white/10 hover:bg-white/20 border-white/30 text-white"
            >
              <Shield className="h-4 w-4 mr-1" />
              Staff
            </Button>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => navigate('/admin/identity-verification')}
              className="bg-white/10 hover:bg-white/20 border-white/30 text-white"
            >
              <FileCheck className="h-4 w-4 mr-1" />
              Identity
            </Button>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => navigate('/admin/document-verification')}
              className="bg-white/10 hover:bg-white/20 border-white/30 text-white"
              data-testid="admin-nav-doc-verification"
            >
              <FileCheck className="h-4 w-4 mr-1" />
              Documents
            </Button>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => navigate('/admin/roles')}
              className="bg-white/10 hover:bg-white/20 border-white/30 text-white"
            >
              <UserCog className="h-4 w-4 mr-1" />
              Roles
            </Button>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => navigate('/admin/2fa-settings')}
              className="bg-white/10 hover:bg-white/20 border-white/30 text-white"
            >
              <Smartphone className="h-4 w-4 mr-1" />
              2FA
            </Button>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => navigate('/admin/ai-control')}
              className="bg-white/10 hover:bg-white/20 border-white/30 text-white"
            >
              <Bot className="h-4 w-4 mr-1" />
              AI Control
            </Button>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => navigate('/admin/content-moderation')}
              className="bg-white/10 hover:bg-white/20 border-white/30 text-white"
            >
              <AlertTriangle className="h-4 w-4 mr-1" />
              Moderation
            </Button>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => navigate('/admin/otp-settings')}
              className="bg-white/10 hover:bg-white/20 border-white/30 text-white"
            >
              <Shield className="h-4 w-4 mr-1" />
              OTP & Email
            </Button>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => navigate('/admin/settings')}
              className="bg-white/10 hover:bg-white/20 border-white/30 text-white"
              data-testid="admin-nav-settings"
            >
              <Settings className="h-4 w-4 mr-1" />
              Settings
            </Button>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => navigate('/admin/vehicles')}
              className="bg-green-500/80 hover:bg-green-500 border-green-400 text-white"
            >
              <Car className="h-4 w-4 mr-1" />
              Vehicles ✅
            </Button>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => navigate('/admin/finance')}
              className="bg-emerald-500/80 hover:bg-emerald-500 border-emerald-400 text-white"
              data-testid="admin-finance-btn"
            >
              <Banknote className="h-4 w-4 mr-1" />
              Finance ✅
            </Button>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => navigate('/admin/insurance')}
              className="bg-indigo-500/80 hover:bg-indigo-500 border-indigo-400 text-white"
              data-testid="admin-insurance-btn"
            >
              <Shield className="h-4 w-4 mr-1" />
              Insurance ✅
            </Button>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => navigate('/admin/marketplace')}
              className="bg-purple-500/80 hover:bg-purple-500 border-purple-400 text-white"
              data-testid="admin-marketplace-btn"
            >
              <PieChart className="h-4 w-4 mr-1" />
              Marketplace
            </Button>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => navigate('/admin/services')}
              className="bg-pink-500/80 hover:bg-pink-500 border-pink-400 text-white"
              data-testid="admin-services-btn"
            >
              <Scissors className="h-4 w-4 mr-1" />
              Services ✅
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium flex items-center justify-between">
                <span>Total Users</span>
                <Users className="h-5 w-5" />
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{stats?.total_users || 0}</div>
              <p className="text-xs text-blue-100 mt-1">
                +{stats?.new_users || 0} new this month
              </p>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium flex items-center justify-between">
                <span>Total Listings</span>
                <Package className="h-5 w-5" />
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{stats?.total_listings || 0}</div>
              <p className="text-xs text-green-100 mt-1">
                {stats?.active_listings || 0} active
              </p>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium flex items-center justify-between">
                <span>Total Views</span>
                <Eye className="h-5 w-5" />
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{stats?.total_views || 0}</div>
              <p className="text-xs text-purple-100 mt-1">
                Across all listings
              </p>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-orange-500 to-orange-600 text-white">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium flex items-center justify-between">
                <span>Pending</span>
                <TrendingUp className="h-5 w-5" />
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{stats?.pending_listings || 0}</div>
              <p className="text-xs text-orange-100 mt-1">
                Awaiting approval
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="users" className="space-y-6">
          <TabsList>
            <TabsTrigger value="users">Users</TabsTrigger>
            <TabsTrigger value="listings">Listings</TabsTrigger>
            <TabsTrigger value="analytics">Analytics</TabsTrigger>
            <TabsTrigger value="activity">Activity Logs</TabsTrigger>
          </TabsList>

          {/* Users Tab */}
          <TabsContent value="users" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span>User Management</span>
                  <div className="flex items-center space-x-2">
                    <Button size="sm" variant="outline" onClick={handleExportUsers}>
                      <Download className="h-4 w-4 mr-2" />
                      Export
                    </Button>
                    <Search className="h-4 w-4 text-gray-400" />
                    <Input 
                      placeholder="Search users..." 
                      className="w-64"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left p-3">Name</th>
                        <th className="text-left p-3">Email</th>
                        <th className="text-left p-3">Phone</th>
                        <th className="text-left p-3">Status</th>
                        <th className="text-left p-3">Verification</th>
                        <th className="text-left p-3">Joined</th>
                        <th className="text-right p-3">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.filter(u => 
                        u.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                        u.email?.toLowerCase().includes(searchTerm.toLowerCase())
                      ).map(user => (
                        <tr key={user.id} className="border-b hover:bg-gray-50">
                          <td className="p-3 font-medium">{user.name}</td>
                          <td className="p-3">{user.email}</td>
                          <td className="p-3">{user.phone}</td>
                          <td className="p-3">{getStatusBadge(user.status)}</td>
                          <td className="p-3">
                            <div className="flex gap-1">
                              {user.email_verified && <CheckCircle className="h-4 w-4 text-green-500" title="Email verified" />}
                              {user.phone_verified && <CheckCircle className="h-4 w-4 text-blue-500" title="Phone verified" />}
                              {user.verified_badge && <Shield className="h-4 w-4 text-purple-500" title="Verified user" />}
                            </div>
                          </td>
                          <td className="p-3">
                            {new Date(user.created_at).toLocaleDateString()}
                          </td>
                          <td className="p-3 text-right">
                            <div className="flex gap-2 justify-end">
                              <Button 
                                variant="outline" 
                                size="sm"
                                onClick={() => handleViewUser(user)}
                              >
                                View
                              </Button>
                              <Button 
                                variant="destructive" 
                                size="sm"
                                onClick={() => handleDeleteUser(user.id)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                <div className="flex items-center justify-between mt-6">
                  <p className="text-sm text-gray-600">
                    Page {currentPage} of {totalPages}
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Listings Tab */}
          <TabsContent value="listings" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Listing Management</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left p-3">Title</th>
                        <th className="text-left p-3">Seller</th>
                        <th className="text-left p-3">Category</th>
                        <th className="text-left p-3">Price</th>
                        <th className="text-left p-3">Status</th>
                        <th className="text-right p-3">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {listings.map(listing => (
                        <tr key={listing.id} className="border-b hover:bg-gray-50">
                          <td className="p-3">{listing.title}</td>
                          <td className="p-3">{listing.seller?.name}</td>
                          <td className="p-3 capitalize">{listing.category}</td>
                          <td className="p-3">{formatCurrency(parseInt(listing.price))}</td>
                          <td className="p-3">
                            <Badge variant={listing.status === 'active' ? 'default' : 'secondary'}>
                              {listing.status}
                            </Badge>
                          </td>
                          <td className="p-3 text-right">
                            <Button 
                              variant="destructive" 
                              size="sm"
                              onClick={() => handleDeleteListing(listing.id)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Analytics Tab */}
          <TabsContent value="analytics" className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Registration Analytics */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <BarChart3 className="h-5 w-5 mr-2" />
                    Registration Sources (30 days)
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {analytics?.registration?.by_source?.length > 0 ? (
                    <div className="space-y-3">
                      {analytics.registration.by_source.map((item, index) => (
                        <div key={index} className="flex items-center justify-between">
                          <span className="capitalize">{item.source}</span>
                          <Badge>{item.count}</Badge>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500">No data available</p>
                  )}
                </CardContent>
              </Card>

              {/* User Behavior */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <Activity className="h-5 w-5 mr-2" />
                    User Behavior (30 days)
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {analytics?.behavior ? (
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <span>Active Users</span>
                        <Badge>{analytics.behavior.active_users_count}</Badge>
                      </div>
                      <div className="flex items-center justify-between">
                        <span>Total Logins</span>
                        <Badge>{analytics.behavior.total_logins}</Badge>
                      </div>
                      <div className="flex items-center justify-between">
                        <span>Total Ad Views</span>
                        <Badge>{analytics.behavior.total_ad_views}</Badge>
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500">No data available</p>
                  )}
                </CardContent>
              </Card>

              {/* Top Contributors */}
              <Card className="md:col-span-2">
                <CardHeader>
                  <CardTitle>Top Ad Posters (30 days)</CardTitle>
                </CardHeader>
                <CardContent>
                  {analytics?.behavior?.top_ad_posters?.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b">
                            <th className="text-left p-2">Name</th>
                            <th className="text-left p-2">Email</th>
                            <th className="text-right p-2">Listings</th>
                          </tr>
                        </thead>
                        <tbody>
                          {analytics.behavior.top_ad_posters.map((poster, index) => (
                            <tr key={index} className="border-b">
                              <td className="p-2">{poster.name}</td>
                              <td className="p-2">{poster.email}</td>
                              <td className="p-2 text-right">
                                <Badge>{poster.listings_count}</Badge>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500">No data available</p>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Activity Tab */}
          <TabsContent value="activity" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Activity className="h-5 w-5 mr-2" />
                  Recent Activity
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {activities.map((activity, index) => (
                    <div key={index} className="flex items-start space-x-4 pb-4 border-b last:border-0">
                      <div className="w-2 h-2 bg-blue-500 rounded-full mt-2" />
                      <div className="flex-1">
                        <p className="font-medium">{activity.user}</p>
                        <p className="text-sm text-gray-600">
                          Created listing: <span className="font-medium">{activity.title}</span>
                        </p>
                        <p className="text-xs text-gray-400 mt-1">
                          {new Date(activity.timestamp).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* User Details Modal */}
      <UserDetailsModal 
        user={selectedUser}
        isOpen={showUserModal}
        onClose={() => {
          setShowUserModal(false);
          setSelectedUser(null);
        }}
        onUpdate={fetchDashboardData}
      />
    </div>
  );
};

export default AdminDashboard;
